import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'antd';
import { Typography } from 'antd';
import { connect } from 'react-redux';
import styles from './RaiseEscalationModal.module.less';
import Modal from '../Modal/Modal';
import { Button } from '../Button/Button';
import RaiseEscalationForm from '../RaiseEscalationForm/RaiseEscalationForm';
import { CreateDisputeRequest } from '../../api/models/dispute';

export type RaiseEscalationModalProps = {
  visible: boolean;
  referenceId: string;
  onClose: () => void;
  createDispute: (data: CreateDisputeRequest) => Promise<any>;
  defaultLanguage: string;
};

export const RaiseEscalationModal = (props: RaiseEscalationModalProps) => {
  const { visible, referenceId, onClose, createDispute, defaultLanguage } = props;
  const { t } = useTranslation();
  const [categoryName, setCategoryName] = useState('');
  const [description, setDescription] = useState('');
  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEscalationError, setIsEscalationError] = useState(false);
  const [isSuccessfullySubmitted, setIsSuccessfullySubmitted] = useState(false);
  const [modalName, setModalName] = useState(`${t('modal.raiseEscalation.title')}`);
  useEffect(() => {
    if (categoryName && description) {
      setIsFormValid(true);
      return;
    }
    setIsFormValid(false);
  }, [categoryName, description]);

  const onCategoryChange = (category: string) => {
    setCategoryName(category);
  };

  const onDescriptionChange = (description: string) => {
    setDescription(description);
  };

  const onSuccess = useCallback(() => {
    setIsSubmitting(false);
    setIsSuccessfullySubmitted(true);
    setIsEscalationError(false);
    setModalName(`${t('modal.raiseEscalation.titleSuccess')}`);
  }, [defaultLanguage]);

  const onError = useCallback(() => {
    setIsSubmitting(false);
    setIsSuccessfullySubmitted(false);
    setIsEscalationError(true);
    setModalName(`${t('modal.raiseEscalation.titleError')}`);
  }, [defaultLanguage]);

  const onReSubmitClick = useCallback(() => {
    setIsSubmitting(false);
    setIsSuccessfullySubmitted(false);
    setIsEscalationError(false);
    setModalName(`${t('modal.raiseEscalation.title')}`);
  }, [defaultLanguage]);

  const onSubmitClick = useCallback(() => {
    if (isFormValid) {
      setIsSubmitting(true);
      createDispute({
        deliveryId: referenceId,
        disputeCategoryName: categoryName,
        message: description,
        lang: defaultLanguage
      })
        .then(() => {
          onSuccess();
        })
        .catch(() => {
          onError();
        });
    }
  }, [isFormValid, createDispute, setIsSubmitting, referenceId, categoryName, description, defaultLanguage]);

  const footer = (
    <Row>
      <Col xs={24}>
        {!isEscalationError && !isSuccessfullySubmitted && (
          <Button
            type="primary"
            disabled={isSubmitting || !isFormValid}
            onClick={onSubmitClick}
            htmlType="submit"
            block
            data-testid="submitButton"
          >
            {t('modal.raiseEscalation.submit')}
          </Button>
        )}
        {isSuccessfullySubmitted && (
          <Button type="primary" onClick={onClose} block data-testid="okButton">
            {t('modal.raiseEscalation.okButton')}
          </Button>
        )}
        {isEscalationError && (
          <>
            <Button type="primary" onClick={onReSubmitClick} block data-testid="reSubmitButton">
              {t('modal.raiseEscalation.reSubmitButton')}
            </Button>
            <Button type={'link'} onClick={onClose} block data-testid="cancelButton">
              {t('modal.raiseEscalation.cancelButton')}
            </Button>
          </>
        )}
      </Col>
    </Row>
  );

  return (
    <Modal titleText={modalName} className={styles.modal} visible={visible} onClose={onClose} footer={footer}>
      <div className={styles.content}>
        {!isEscalationError && !isSuccessfullySubmitted && (
          <RaiseEscalationForm
            onCategoryChange={onCategoryChange}
            onDescriptionChange={onDescriptionChange}
            initCategoryName={categoryName}
            initDescription={description}
          />
        )}
        {isSuccessfullySubmitted && <Typography.Text>{t('modal.raiseEscalation.textSuccess')}</Typography.Text>}
        {isEscalationError && <Typography.Text>{t('modal.raiseEscalation.textError')}</Typography.Text>}
      </div>
    </Modal>
  );
};

const mapState = ({
  user: {
    userSettings: { defaultLanguage }
  }
}: any) => ({ defaultLanguage });

const mapDispatch = ({ disputes: { createDispute } }: any) => ({
  createDispute
});

export default connect(mapState, mapDispatch)(RaiseEscalationModal);
